import * as d3 from 'd3-selection'
import * as dataset from './dataset.js'
import keycodes from './keycodes.js'

export default function (selection, forceChangeEvent) {
  selection.each(function (data) {
    let placeholder = {dataset: {'filtering-default': true}}
    // Set the placeholder node option's value to be the search term
    placeholder[data.accessors.value] = data.combo ? data.searchTerm : null

    let select = d3.select(this)
                   .style('inset-inline-start', '-10000px')
                   .style('position', 'fixed')
                   .style('width', 0) // See https://github.com/SpiderStrategies/Scoreboard/issues/53972
                   .attr('tabindex', -1)
                   .attr('aria-hidden', false) // Show for screen readers
                   .property('multiple', function (d) {
                     return d.multiple
                   })
                   .on('focus', function () {
                     /*
                      * Since this native select is off the screen, it should never
                      * get focus, but ios 10 safari proves otherwise, and it shows a tooltip (offscreen)
                      * that intercepts the next tap on the window. This removes focus on the select if it ever
                      * receives focus. See https://github.com/SpiderStrategies/scoreboard-select/issues/188
                      */
                      this.blur()
                      // If this select gets focus, give focus to the rich select textarea
                      this.nextSibling.querySelector('.select-control textarea').focus()
                   })
      , frag = document.createDocumentFragment()
      , cloneable = document.createElement('option')

    let options = select.selectAll('option')
                        .data(function () {
                          // If native support is turned off, we only draw the `option` elements that
                          // are selected
                          let opts = data.nativeSupport ? data.options : data.options.filter(o => o.selected)

                          if (data.filterable) {
                            // If it's a filtering select, and there isn't a selected value, we want to
                            // make sure there is an empty null option, so the native select's value doesn't
                            // default to the first option in the list
                            return [placeholder].concat(opts)
                          } else {
                            return opts || []
                          }
                        })
      , enter = options.enter()
                       .select(function (d) {
                         // Append each new node to the document fragment
                         let node = cloneable.cloneNode(false) // shallow clone
                         frag.appendChild(node) // Append to the document fragment right away, it's faster than manipulating attributes then appending

                         node.selected = d.selected // Sets property so `select.value` code works
                         if (d.selected) {
                           node.setAttribute('selected','selected') // Sets attr selected so querySelectors work
                         }

                         if (d[data.accessors.value] !== null) {
                           node.value = d[data.accessors.value]
                         }

                         node.__data__ = d // Hack to bind the d3 data
                         return node
                       })

    select.node().appendChild(frag) // Add the enter group, so we have what's missing

    // Give the browser a moment and then set all the data-* attributes. This seems to slow down IE by a few hundred milliseconds and there's no need for immediately
    if (enter.size()) {
      window.requestAnimationFrame(() => {
        enter.select(function (d) {
          let node = d3.select(this)

          for (const [key, value] of Object.entries(dataset.expand(d.dataset))) {
            node.attr(key, value)
          }
        })
      })
    }

    options.exit()
           .remove()

    // Now do updates for whatever is already in the DOM
    options.property('value', function (d) {
             return d[data.accessors.value]
           })
           .property('selected', function (d, i) {
             return !!d.selected
           })
           .attr('selected', function (d) {
             return d.selected ? 'selected' : null
           })
           .select(function (d) {
             if (d.dataset) {
               let node = d3.select(this)

               for (const [key, value] of Object.entries(dataset.expand(d.dataset))) {
                 node.attr(key, value)
               }
             }
           })

    if (forceChangeEvent) {
      // Send 'change' event to real select - to trigger any change event listeners
      var e = document.createEvent('Event')
      e.initEvent('change', true, true)
      e._rich = true
      select.node().dispatchEvent(e)
    }
  })
}
